import type React from 'react';
import { SiteNickname } from '@unisporkal/sites';
import {
  ButtonVariantEnum,
  KeywordLinkGroupItemVariantEnum,
} from '@unisporkal/alliance-ui';
import { GettyBoardButton, IstockBoardButton } from '@unisporkal/board-toolbox';
import {
  ALL_ASSET_TYPE_SEARCHES,
  AllowedUseSearchFilterEnum,
  AssetFamilyEnum,
  AssetFamilySearchFilterEnum,
  AssetTypeEnum,
  FILM_SEARCHES,
  FileTypeEnum,
  IMAGE_FILM_OR_BOTH_SEARCHES,
  IMAGE_INCLUDING_ASSET_TYPES,
  MediaTypeEnum,
  SRPLayoutEnum,
} from 'app/domain';
import type {
  AppliedFilterSiteExclusions,
  Asset,
  SearchParameterSiteExclusions,
} from 'app/domain';
import marketingGetty from 'app/react/getty/assets/marketing-getty.png';
import marketingGetty2x from 'app/react/getty/assets/marketing-getty@2x.png';
import marketingIstock from 'app/react/istock/assets/marketing-istock.png';
import marketingIstock2x from 'app/react/istock/assets/marketing-istock@2x.png';
import GettyClipBadgeIcon4K from 'app/react/getty/assets/images/clip-badge-4k.svg?icon';
import GettyClipBadgeIcon360 from 'app/react/getty/assets/images/clip-badge-360.svg?icon';
import IstockClipBadgeIcon4K from 'app/react/istock/assets/images/clip-badge-4k.svg?icon';
import {
  gettyEditorialDefaultTiles,
  iStockEditorialDefaultTiles,
} from './editorialDefaultTiles';
import type { EditorialDefaultTilesConfig } from './editorialDefaultTiles';

interface BoardButtonBaseProps {
  asset: Asset;
  uiLocation: string;
}

// Icon-only vs icon+label rendering
type BoardButtonProps =
  | (BoardButtonBaseProps & { showLabel?: true })
  | (BoardButtonBaseProps & { showLabel: false; className?: string });

// @unisporkal/board-toolbox ships no type declarations, so these resolve to
// `any` — cast once here (rather than at each `Component:` assignment below)
// so the rest of the file assigns an explicitly-typed value.
const TypedGettyBoardButton =
  GettyBoardButton as React.ComponentType<BoardButtonProps>;
const TypedIstockBoardButton =
  IstockBoardButton as React.ComponentType<BoardButtonProps>;

// Mirrors EditorialBadgeVariantEnum's values (config must not depend on
// `shared/logicalComponents`, so this is a config-local copy, not an import
// of the real enum) — the consuming component maps this to the real
// discriminated union.
export enum MosaicEditorialBadgeVariantEnum {
  TEXT_LABEL = 'textLabel',
  ICON = 'icon',
}

export type MosaicEditorialBadgeConfig =
  | { variant: MosaicEditorialBadgeVariantEnum.TEXT_LABEL; labelKey: string }
  | { variant: MosaicEditorialBadgeVariantEnum.ICON };

// Where the sites differ on putting the search phrase on a tile's ADP link;
// see shouldIncludePhraseParam.
export type PhraseParamConfig = {
  // Editorial film carries the phrase so the ADP's editorial similar clips
  // can use it. Creative film never does.
  forEditorialFilm: boolean;
  forBotsAndFirstTimeVisitors: boolean;
};

export enum ModificationToolKeyEnum {
  EXTEND_IMAGE = 'extend_image',
  ADD_OR_REMOVE_OBJECTS = 'add_or_remove_objects',
  REPLACE_BACKGROUND = 'replace_background',
  REMOVE_BACKGROUND = 'remove_background',
  PRODUCT_BACKGROUND = 'use_for_product_background',
  REFERENCE_IMAGE = 'use_as_reference_for_new_image',
}

export enum MerchandisingBannersEnum {
  AI_BUNDLE_ACTIVE = 'ai_bundle_active',
  AI_BUNDLE_OFFER_PREMIUM = 'ai_bundle_offer_premium',
  DISCOVER_GENERATIVE_AI = 'discover_generative_ai',
  DISCOVER_UNLIMITED_AI = 'discover_unlimited_ai',
  PREMIUM_UPSELL = 'premium_upsell',
}

export enum MerchandisingTileEnum {
  AI_MODIFICATION = 'ai_modification',
  CREATIVE_LIBRARY = 'creative_library',
}

export enum FilterPanelOpenIconVariantEnum {
  FILLED = 'filled',
  HOLLOW = 'hollow',
}

// What the search bar does once scrolled past the header.
export enum SearchBarScrollBehaviourEnum {
  // Sticks, with the line under the search bar (related terms, scrolled row).
  STICKS = 'sticks',
  // Sticks with the search bar alone.
  STICKS_BARE = 'sticksBare',
  // Remains docked; the search bar comes back on scroll-up.
  PEEKS = 'peeks',
  // Remains docked and scrolls off with the page.
  SCROLLS_AWAY = 'scrollsAway',
}

// The row the full search bar shows under itself once scrolled past the header.
export enum SearchBarScrolledRowEnum {
  NAV_BAR = 'navBar',
  FILTERS_BAR = 'filtersBar',
}

export enum ActionButtonPositionEnum {
  BOTTOM_RIGHT = 'bottom_right',
  TOP_RIGHT = 'top_right',
}

// Rows of the metadata list beneath a fixed-layout tile's title. Each row
// renders only when the asset carries the data it needs.
export enum FixedAssetMetadataRowEnum {
  PEOPLE = 'people',
  // "By" label plus the artist name, always clickable.
  ARTIST = 'artist',
  // Contributor display name (falling back to the artist), not clickable on
  // video templates.
  CONTRIBUTOR = 'contributor',
  DATE_CREATED = 'dateCreated',
  CLIP_LENGTH = 'clipLength',
  COLLECTION_NAME = 'collectionName',
  ASSET_ID = 'assetId',
}

interface AssetToIloxLabelMatcher {
  assetType?: AssetTypeEnum;
  assetFamily?: AssetFamilyEnum;
  mediaType?: MediaTypeEnum;
  fileType?: FileTypeEnum;
}

export interface AssetToIloxLabelMapping {
  matcher: AssetToIloxLabelMatcher;
  iloxKey: string;
}

// Site-divergent display labels for applied-filter chips. Most chip labels are
// derived from a shared translation-key convention, but a couple of parameters
// resolve to different strings per site, so those keys live here.
export type AppliedFilterLabelConfig = {
  // Contributor/artist chip label, keyed by asset type. `default` applies when
  // the asset type has no specific entry (iStock uses one label for every type).
  artistExact: Readonly<Record<string, string>>;
  // File-size chip label, keyed by the size value. Sites use different values
  // and translation keys, so there is no shared default.
  imagesize: Readonly<Record<string, string>>;
};

export type SiteConfig = {
  banners: {
    showAgreementsBanner: boolean;
    showPortfolioBanner: boolean;
    showSingleSeatSubscriptionBanner: boolean;
  };
  batchDownload: {
    available: boolean;
  };
  showCollectionTabs: boolean;
  hasHorizontalFiltersBar: boolean;
  showAssetCountDisplay: boolean;
  galleryFooter: {
    showClarifications: boolean;
    showScrollToTopButton: boolean;
    pagination: {
      buttonVariant: ButtonVariantEnum;
      translationKeys: {
        nextButtonLabel: string;
        previousButtonLabel: string;
      };
    };
  };
  gallery: {
    seoLicenseUrlPathRef: string; // parameter for siteMap.pathRef() to get SEO license URL
    // Whether editorial natural-language results interleave keyword matches,
    // which the NLS sort-by notice and the gallery-header ribbon describe.
    hasNLSKeywordInterleave: boolean;
    similarButton: {
      buttonVariant: ButtonVariantEnum;
      // Hide the button when the asset is part of the analog archive collection.
      hideOnAnalogArchive: boolean;
      // Layouts on which the click handler dispatches `assettype` alongside
      // `family` and `morelikethis`. Empty array = never dispatch. Pivots the
      // result set by asset type as well as family. Currently only iStock Mosaic.
      setAssetTypeOn: ReadonlyArray<SRPLayoutEnum>;
    };
    // Shared across the Gallery logical component's asset-tile overlays
    // (MosaicAssetOverlay, FixedAssetOverlay) rather than each declaring its
    // own section.
    assetOverlay: {
      showCartButton: boolean;
      showDownloadButton: boolean;
      // iStock never shows the request-preview button. On Getty it can be
      // shown, gated by whether the asset is part of the analog archive
      // collection (Rights-Managed archival photography).
      showRequestPreviewButton: boolean;
      boardButton: {
        Component: React.ComponentType<BoardButtonProps>;
      };
    };
    translationKeys: {
      seoDescriptionLabelMapping: Array<AssetToIloxLabelMapping>;
      seoTitleLabelMapping: Array<AssetToIloxLabelMapping>;
    };
  };
  mosaicAssetOverlay: {
    actionButtonPosition: ActionButtonPositionEnum;
    editorialBadge: MosaicEditorialBadgeConfig;
  };
  fixedAsset: {
    // Whether an editorial-use-only asset carries an icon on its thumbnail.
    // Getty marks it in the license row below the thumbnail instead.
    showsEditorialIcon: boolean;
  };
  fixedAssetOverlay: {
    // Whether similar/download/modification-tools join the
    // cart/request-preview/board cluster as one adjacent, right-aligned
    // group (istock legacy's `.containerRight`, `margin-left: auto`), or
    // render as their own separate group to its left (getty legacy's
    // 4-column grid, `.actionsItem` apart from `.actionsList`). A
    // FixedAssetOverlay layout fact, not a per-button config value.
    groupActionsTogether: boolean;
  };
  fixedAssetContent: {
    // Whether a license row (badge, license scope) sits above the title.
    showsLicenseInfo: boolean;
    // Whether the title is a link to the asset's landing page.
    linksTitleToAsset: boolean;
    // Keep the title and each metadata row to one line, clipped with an
    // ellipsis, rather than wrapping.
    truncatesText: boolean;
    metadataRows: ReadonlyArray<FixedAssetMetadataRowEnum>;
  };
  clipBadge: {
    supports360: boolean;
    supportsOfflineDowngrade: boolean;
    Icon4K: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    Icon360?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  merchandising: {
    defaultBanner: MerchandisingBannersEnum;
  };
  // The in-grid merchandising tile spliced into the mosaic SRP.
  merchandisingTile: MerchandisingTileEnum;
  modificationToolsMenu:
    | {
        enabled: false;
      }
    | {
        enabled: true;
        modifyButtonVariant: ButtonVariantEnum;
      };
  signatureCrown: {
    enabled: boolean;
  };
  // CSS custom property references (e.g. 'var(--grey-700)'), not raw hex —
  // picked per site from @unisporkal/alliance-ui's baseColors as the closest
  // match to each site's existing AssetPreview header/caption colors.
  assetPreview: {
    headerColor: string;
    captionColor: string;
  };
  // The link from a gallery tile to its ADP, shared by the mosaic and fixed
  // tiles.
  assetLink: {
    // Who gets the search phrase on the link, which filters the ADP's Similar
    // Images. Both sites add it to images (see shouldIncludePhraseParam).
    // Getty: editorial film too, not bots or first-time visitors.
    // iStock: images only, bots and first-time visitors included.
    phraseParam: PhraseParamConfig;
    // Getty: a click opens the ADP popup in a tab named for the asset, or
    // runs an events search when the gallery is grouped by event. iStock:
    // plain navigation, no handler.
    opensAdpPopup: boolean;
  };
  mosaicAsset: {
    // Getty fires the impression callback once per asset; iStock can fire it
    // multiple times (see MR !3925, note_2175963). Getty: true. iStock: false.
    impressionTrackingTriggerOnce: boolean;
  };
  relatedTerms: {
    termItemLinkVariant: KeywordLinkGroupItemVariantEnum;
  };
  searchBar: {
    allowBlankSearches: boolean;
    hideOnScroll: boolean;
    overlayOnFocus: boolean;
    overlayOnFocusAndDocked: boolean;
  };
  searchBarLayout: {
    // Scroll offset (px) past which the bar counts as scrolled.
    scrollThreshold: number;
    // Shows the refine button beside the search bar when no line sits under
    // it; a line under the search bar always starts with the refine button.
    showRefineButtonInSearchBar: boolean;
    scrolledRow: {
      kind: SearchBarScrolledRowEnum;
    };
    // On simple-layout pages (gateways, zero results, recently viewed).
    simpleLayoutScrollBehaviour:
      | SearchBarScrollBehaviourEnum.STICKS_BARE
      | SearchBarScrollBehaviourEnum.PEEKS;
  };
  filterPanel: {
    hasNavigationRow: boolean;
    switchInverse: boolean;
    collapsibleInverse: boolean;
    colorSelectorInverse: boolean;
  };
  refineFiltersButton: {
    showLeftArrow: boolean;
    openButtonVariant: FilterPanelOpenIconVariantEnum;
    labelKey: string;
    showAppliedFiltersLabel: boolean;
  };
  refineFiltersMobileButton: {
    labelKey: string;
    openButtonVariant: FilterPanelOpenIconVariantEnum;
  };
  signInPrompt: {
    benefitTranslationKeys: string[];
    marketingImage: string;
    marketingImage2x: string;
    primaryButtonVariant: ButtonVariantEnum;
  };
  appliedFilters: AppliedFilterSiteExclusions;
  searchParameters: SearchParameterSiteExclusions;
  appliedFilterLabels: AppliedFilterLabelConfig;
  editorialDefaultTiles: EditorialDefaultTilesConfig;
};

const gettyConfig: SiteConfig = {
  banners: {
    showAgreementsBanner: true,
    showPortfolioBanner: false,
    showSingleSeatSubscriptionBanner: true,
  },
  batchDownload: {
    available: true,
  },
  showCollectionTabs: false,
  hasHorizontalFiltersBar: false,
  showAssetCountDisplay: true,
  galleryFooter: {
    showClarifications: true,
    showScrollToTopButton: false,
    pagination: {
      buttonVariant: ButtonVariantEnum.HOLLOW,
      translationKeys: {
        nextButtonLabel: 'gallery.next_uppercase',
        previousButtonLabel: 'gallery.previous_uppercase',
      },
    },
  },
  gallery: {
    seoLicenseUrlPathRef: 'licenseOverview',
    hasNLSKeywordInterleave: true,
    similarButton: {
      buttonVariant: ButtonVariantEnum.OVERLAY_TERTIARY,
      hideOnAnalogArchive: true,
      setAssetTypeOn: [],
    },
    assetOverlay: {
      showCartButton: true,
      showDownloadButton: true,
      showRequestPreviewButton: true,
      boardButton: {
        Component: TypedGettyBoardButton,
      },
    },
    translationKeys: {
      seoDescriptionLabelMapping: [
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.EDITORIAL,
          },
          iloxKey: 'download_stock_footage_of',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.CREATIVE,
          },
          iloxKey: 'download_stock_videos_of',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.EDITORIAL,
          },
          iloxKey: 'download_premium_news_photos',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.PHOTOGRAPHY,
          },
          iloxKey: 'download_stock_photos_of',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
            fileType: FileTypeEnum.EPS,
          },
          iloxKey: 'download_vector_images',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
          },
          iloxKey: 'download_vector_illustrations_of',
        },
      ],
      seoTitleLabelMapping: [
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.EDITORIAL,
          },
          iloxKey: 'video_footage_with_caption',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.CREATIVE,
          },
          iloxKey: 'stock_video_footage',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.EDITORIAL,
          },
          iloxKey: 'news_photo_with_caption',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.PHOTOGRAPHY,
          },
          iloxKey: 'seo_stock_photo',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
            fileType: FileTypeEnum.EPS,
          },
          iloxKey: 'seo_title_stock_vector_image',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
          },
          iloxKey: 'stock_vector_illustration',
        },
      ],
    },
  },
  mosaicAssetOverlay: {
    actionButtonPosition: ActionButtonPositionEnum.BOTTOM_RIGHT,
    editorialBadge: {
      variant: MosaicEditorialBadgeVariantEnum.TEXT_LABEL,
      labelKey: 'intended_for_editorial',
    },
  },
  fixedAsset: {
    showsEditorialIcon: false,
  },
  fixedAssetOverlay: {
    groupActionsTogether: false,
  },
  fixedAssetContent: {
    showsLicenseInfo: true,
    linksTitleToAsset: false,
    truncatesText: false,
    metadataRows: [
      FixedAssetMetadataRowEnum.PEOPLE,
      FixedAssetMetadataRowEnum.ARTIST,
      FixedAssetMetadataRowEnum.DATE_CREATED,
      FixedAssetMetadataRowEnum.CLIP_LENGTH,
      FixedAssetMetadataRowEnum.COLLECTION_NAME,
      FixedAssetMetadataRowEnum.ASSET_ID,
    ],
  },
  clipBadge: {
    supports360: true,
    supportsOfflineDowngrade: true,
    Icon4K: GettyClipBadgeIcon4K,
    Icon360: GettyClipBadgeIcon360,
  },
  filterPanel: {
    hasNavigationRow: true,
    switchInverse: false,
    collapsibleInverse: false,
    colorSelectorInverse: false,
  },
  refineFiltersButton: {
    showLeftArrow: true,
    openButtonVariant: FilterPanelOpenIconVariantEnum.FILLED,
    labelKey: 'filters',
    showAppliedFiltersLabel: true,
  },
  refineFiltersMobileButton: {
    labelKey: 'filters',
    openButtonVariant: FilterPanelOpenIconVariantEnum.FILLED,
  },
  merchandising: {
    defaultBanner: MerchandisingBannersEnum.DISCOVER_GENERATIVE_AI,
  },
  merchandisingTile: MerchandisingTileEnum.AI_MODIFICATION,
  modificationToolsMenu: {
    enabled: false,
  },
  signatureCrown: {
    enabled: false,
  },
  assetPreview: {
    headerColor: 'var(--grey-700)',
    captionColor: 'var(--grey-600)',
  },
  assetLink: {
    phraseParam: {
      forEditorialFilm: true,
      forBotsAndFirstTimeVisitors: false,
    },
    opensAdpPopup: true,
  },
  mosaicAsset: {
    impressionTrackingTriggerOnce: true,
  },
  relatedTerms: {
    termItemLinkVariant: KeywordLinkGroupItemVariantEnum.BRANDED,
  },
  searchBar: {
    allowBlankSearches: true,
    hideOnScroll: true,
    overlayOnFocus: true,
    overlayOnFocusAndDocked: true,
  },
  searchBarLayout: {
    scrollThreshold: 185,
    showRefineButtonInSearchBar: false,
    scrolledRow: {
      kind: SearchBarScrolledRowEnum.NAV_BAR,
    },
    simpleLayoutScrollBehaviour: SearchBarScrollBehaviourEnum.PEEKS,
  },
  signInPrompt: {
    benefitTranslationKeys: [
      'sign_in_prompt_getty_benefit_downloads_subscriptions_purchases',
      'sign_in_prompt_getty_benefit_history_licenses_invoices',
      'sign_in_prompt_benefit_faster_checkout',
    ],
    marketingImage: marketingGetty,
    marketingImage2x: marketingGetty2x,
    primaryButtonVariant: ButtonVariantEnum.PRIMARY,
  },
  appliedFilters: {
    excludedParams: ['alloweduse'],
    excludedValues: {},
  },
  searchParameters: {
    excludedParams: ['istockcollection', 'contributor', 'istockexclusive'],
  },
  appliedFilterLabels: {
    artistExact: {
      [AssetTypeEnum.IMAGE]: 'search_filters.photographers',
      [AssetTypeEnum.FILM]: 'search_filters.filmmaker',
      [AssetTypeEnum.VIDEO_TEMPLATE]: 'search_filters.creator_uppercase',
    },
    imagesize: {
      xlarge: 'search_filters.imagesize_12_mp',
      xxlarge: 'search_filters.imagesize_16_mp',
      xxxlarge: 'search_filters.imagesize_21_mp',
    },
  },
  editorialDefaultTiles: gettyEditorialDefaultTiles,
};

const iStockConfig: SiteConfig = {
  banners: {
    showAgreementsBanner: false,
    showPortfolioBanner: true,
    showSingleSeatSubscriptionBanner: false,
  },
  batchDownload: {
    available: false,
  },
  showCollectionTabs: true,
  hasHorizontalFiltersBar: true,
  showAssetCountDisplay: false,
  galleryFooter: {
    showClarifications: false,
    showScrollToTopButton: true,
    pagination: {
      buttonVariant: ButtonVariantEnum.TERTIARY,
      translationKeys: {
        nextButtonLabel: 'gallery.next',
        previousButtonLabel: 'gallery.previous',
      },
    },
  },
  gallery: {
    seoLicenseUrlPathRef: 'licenseAgreement',
    hasNLSKeywordInterleave: false,
    similarButton: {
      buttonVariant: ButtonVariantEnum.OVERLAY,
      hideOnAnalogArchive: false,
      setAssetTypeOn: [SRPLayoutEnum.MOSAIC],
    },
    assetOverlay: {
      showCartButton: false,
      showDownloadButton: false,
      showRequestPreviewButton: false,
      boardButton: {
        Component: TypedIstockBoardButton,
      },
    },
    translationKeys: {
      seoDescriptionLabelMapping: [
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.CREATIVE,
          },
          iloxKey: 'download_royalty_free_video_footage',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.PHOTOGRAPHY,
          },
          iloxKey: 'download_royalty_free_stock_photos',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
            fileType: FileTypeEnum.EPS,
          },
          iloxKey: 'download_asset_title_vector_images',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
          },
          iloxKey: 'download_royalty_free_illustrations',
        },
      ],
      seoTitleLabelMapping: [
        {
          matcher: {
            assetType: AssetTypeEnum.FILM,
            assetFamily: AssetFamilyEnum.CREATIVE,
          },
          iloxKey: 'stock_video_footage',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.PHOTOGRAPHY,
          },
          iloxKey: 'seo_stock_photo',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
            fileType: FileTypeEnum.EPS,
          },
          iloxKey: 'seo_title_stock_vector_image',
        },
        {
          matcher: {
            assetType: AssetTypeEnum.IMAGE,
            assetFamily: AssetFamilyEnum.CREATIVE,
            mediaType: MediaTypeEnum.ILLUSTRATION,
          },
          iloxKey: 'stock_vector_illustration',
        },
      ],
    },
  },
  mosaicAssetOverlay: {
    actionButtonPosition: ActionButtonPositionEnum.TOP_RIGHT,
    editorialBadge: {
      variant: MosaicEditorialBadgeVariantEnum.ICON,
    },
  },
  fixedAsset: {
    showsEditorialIcon: true,
  },
  fixedAssetOverlay: {
    groupActionsTogether: true,
  },
  fixedAssetContent: {
    showsLicenseInfo: false,
    linksTitleToAsset: true,
    truncatesText: true,
    metadataRows: [
      FixedAssetMetadataRowEnum.CONTRIBUTOR,
      FixedAssetMetadataRowEnum.ASSET_ID,
    ],
  },
  clipBadge: {
    supports360: false,
    supportsOfflineDowngrade: false,
    Icon4K: IstockClipBadgeIcon4K,
  },
  filterPanel: {
    hasNavigationRow: false,
    switchInverse: true,
    collapsibleInverse: true,
    colorSelectorInverse: true,
  },
  refineFiltersButton: {
    showLeftArrow: false,
    openButtonVariant: FilterPanelOpenIconVariantEnum.HOLLOW,
    labelKey: 'refine',
    showAppliedFiltersLabel: false,
  },
  refineFiltersMobileButton: {
    labelKey: 'refine',
    openButtonVariant: FilterPanelOpenIconVariantEnum.HOLLOW,
  },
  merchandising: {
    defaultBanner: MerchandisingBannersEnum.DISCOVER_UNLIMITED_AI,
  },
  merchandisingTile: MerchandisingTileEnum.CREATIVE_LIBRARY,
  modificationToolsMenu: {
    enabled: true,
    modifyButtonVariant: ButtonVariantEnum.SECONDARY,
  },
  signatureCrown: {
    enabled: true,
  },
  assetPreview: {
    headerColor: 'var(--green-100)',
    captionColor: 'var(--grey-400)',
  },
  assetLink: {
    phraseParam: {
      forEditorialFilm: false,
      forBotsAndFirstTimeVisitors: true,
    },
    opensAdpPopup: false,
  },
  mosaicAsset: {
    impressionTrackingTriggerOnce: false,
  },
  relatedTerms: {
    termItemLinkVariant: KeywordLinkGroupItemVariantEnum.NEUTRAL,
  },
  searchBar: {
    allowBlankSearches: false,
    hideOnScroll: false,
    overlayOnFocus: false,
    overlayOnFocusAndDocked: false,
  },
  searchBarLayout: {
    scrollThreshold: 230,
    showRefineButtonInSearchBar: true,
    scrolledRow: {
      kind: SearchBarScrolledRowEnum.FILTERS_BAR,
    },
    simpleLayoutScrollBehaviour: SearchBarScrollBehaviourEnum.STICKS_BARE,
  },
  signInPrompt: {
    benefitTranslationKeys: [
      'sign_in_prompt_istock_benefit_subscriptions_credits',
      'sign_in_prompt_istock_benefit_discounts_offers',
      'sign_in_prompt_benefit_faster_checkout',
    ],
    marketingImage: marketingIstock,
    marketingImage2x: marketingIstock2x,
    primaryButtonVariant: ButtonVariantEnum.SECONDARY,
  },
  appliedFilters: {
    excludedParams: [],
    excludedValues: {
      // `off` is the reset sentinel the search writes after an alloweduse chip
      // is removed, not a user-chosen filter, so it never surfaces as a chip.
      alloweduse: [AllowedUseSearchFilterEnum.OFF],
    },
  },
  searchParameters: {
    excludedParams: [
      'people',
      'embeddable',
      'suppressfamilycorrection',
      'specialcollectionid',
      'clarifications',
      'editorialsources',
      'releasestatus',
      'imagetechnique',
      'viewpoint',
      'offlinecontent',
      'begindate',
      'enddate',
      'eventid',
      'parenteventid',
      'events',
      'editorialproducts',
      'specificpeople',
      'groupbyevent',
      'license',
      // Getty's agreements and collections, which iStock has no equivalent of.
      'agreements',
      'collections',
      'compositions',
      'curatedset',
      'easyaccess',
      'editorialsubscription',
      'premiumaccess',
      'subPreview',
      'subPreviewSku',
    ],
    // iStock offers these on image, film and combined searches whatever the
    // family — every asset type but a video template. The asset-type/family
    // rules are Getty's: `recency` is its editorial-only date filter, whereas
    // iStock's upload date applies to image and film searches of any family.
    availableOnlyIn: {
      excludenudity: ALL_ASSET_TYPE_SEARCHES,
      istockcollection: IMAGE_FILM_OR_BOTH_SEARCHES,
      contributor: IMAGE_FILM_OR_BOTH_SEARCHES,
      istockexclusive: IMAGE_FILM_OR_BOTH_SEARCHES,
      recency: IMAGE_FILM_OR_BOTH_SEARCHES,
      numberofpeople: IMAGE_FILM_OR_BOTH_SEARCHES,
      alloweduse: [{ assettypes: IMAGE_INCLUDING_ASSET_TYPES }],
      phraseprocessing: [
        {
          assettypes: IMAGE_INCLUDING_ASSET_TYPES,
          families: [
            AssetFamilySearchFilterEnum.CREATIVE,
            AssetFamilySearchFilterEnum.EDITORIAL,
          ],
        },
        ...FILM_SEARCHES,
      ],
    },
  },
  appliedFilterLabels: {
    // iStock shows one contributor label regardless of asset type.
    artistExact: {
      default: 'gallery.contributor',
    },
    imagesize: {
      large: 'search_filters.large_file_size',
      xlarge: 'search_filters.xlarge_file_size',
      xxlarge: 'search_filters.xxlarge_file_size',
    },
  },
  editorialDefaultTiles: iStockEditorialDefaultTiles,
};

const siteConfigRegistry: Record<SiteNickname, SiteConfig> = {
  getty: gettyConfig,
  istock: iStockConfig,
};

export const getSiteConfig = (siteName: SiteNickname): SiteConfig => {
  const siteConfig = siteConfigRegistry[siteName];

  if (!siteConfig) {
    return siteConfigRegistry.getty;
  }

  return siteConfigRegistry[siteName];
};
